import isEmpty from 'lodash.isempty';
import get from 'lodash.get';
import toPairs from 'lodash.topairs';
import {
    BENCHMARK,
    getCommonInterpretation,
} from '../../../constants/shared/CommonInterpretationMapper';
import { MEASURE } from '../../../constants/shared/MeasuresConstants';
import {
    getTrcInterpretation,
    PROFICIENT_LEVEL,
} from '../../../constants/shared/TrcInterpretationMapper';

const { TRC, VOCABO } = MEASURE;
const { AT_OR_ABOVE, AT } = BENCHMARK;

export const parseR3DInterps = r3dResponse => {
    const interpretations = [];
    if (isEmpty(r3dResponse)) return interpretations;

    const trcResponse = get(r3dResponse, TRC, {});
    toPairs(trcResponse.interpsMap).map(([grade, periods]) =>
        toPairs(periods).forEach(([period, interpsByPeriod]) => {
            if (!isEmpty(interpsByPeriod)) {
                const goal = interpsByPeriod.find(
                    item => item.displayName.toLowerCase() === PROFICIENT_LEVEL.PROFICIENT,
                ).threshold;
                const thresholds = interpsByPeriod.map(({ threshold, displayName }) => ({
                    scoreThreshold: threshold,
                    interp: getTrcInterpretation(displayName.toLowerCase()),
                }));
                interpretations.push({
                    measure: trcResponse.assesmentCode,
                    grade,
                    period,
                    goal,
                    thresholds,
                });
            }
        }),
    );
    return interpretations;
};

export const parseDyslexiaInterps = (dyslexiaResponse, measure, dyslexiaInterpBoundaries) => {
    let interpretations = [];
    if (isEmpty(dyslexiaResponse)) return interpretations;
    interpretations = dyslexiaResponse.map(interpData => {
        const goal = interpData.interpretation_thresholds.find(
            item => item.interpretation === AT_OR_ABOVE,
        ).score_threshold;
        const thresholds = interpData.interpretation_thresholds.map(
            ({ score_threshold: scoreThreshold, interpretation }) => ({
                scoreThreshold,
                interp: getCommonInterpretation(interpretation),
            }),
        );
        return {
            measure,
            grade: interpData.grade_level,
            period: interpData.period,
            goal,
            thresholds,
            ...dyslexiaInterpBoundaries(interpData, measure),
        };
    });
    return interpretations;
};

export const parseVocaboInterps = vocaboResponse => {
    let interpretations = [];
    if (isEmpty(vocaboResponse)) return interpretations;
    interpretations = vocaboResponse.map(interpData => {
        const goal = interpData.interpretation_thresholds.find(
            item => item.interpretation === AT_OR_ABOVE,
        ).score_threshold;
        const thresholds = interpData.interpretation_thresholds.map(
            ({ score_threshold: scoreThreshold, interpretation }) => ({
                scoreThreshold,
                interp: getCommonInterpretation(interpretation),
            }),
        );
        return {
            measure: VOCABO,
            grade: interpData.grade_level,
            period: interpData.period,
            goal,
            thresholds,
            maxScore: interpData.max_score,
        };
    });
    return interpretations;
};

export const parseOlInterps = olResponse => {
    let interpretations = [];
    if (isEmpty(olResponse)) return interpretations;

    interpretations = olResponse.map(interpData => {
        const goal = interpData.thresholds.find(
            item => item.interpretation === AT_OR_ABOVE,
        ).score_threshold;
        const thresholds = interpData.thresholds.map(thresholdObject => ({
            scoreThreshold: Number(thresholdObject.score_threshold),
            interp: getCommonInterpretation(thresholdObject.interpretation),
        }));

        return {
            measure: interpData.measure,
            grade: interpData.grade,
            period: interpData.period,
            goal,
            thresholds,
        };
    });
    return interpretations;
};

export const parseInterps = response => {
    let interpretations = [];
    if (isEmpty(response)) return interpretations;
    interpretations = response.map(interpData => {
        const thresholds = interpData.thresholds.map(thresholdObject => ({
            scoreThreshold: thresholdObject.score_threshold,
            interp: getCommonInterpretation(thresholdObject.interpretation),
        }));
        return {
            measure: interpData.measure,
            grade: interpData.grade,
            period: interpData.period,
            goal: interpData.preliminary_goal_score,
            thresholds,
            maxScore: interpData.max_score,
        };
    });
    return interpretations;
};

export const parseBasicComprehensionInterps = basicComprehensionResponse => {
    let interpretations = [];
    if (isEmpty(basicComprehensionResponse)) return interpretations;
    interpretations = basicComprehensionResponse.map(interpData => {
        const goal = interpData.thresholds.find(
            item => item.interpretation === AT,
        )?.score_threshold;
        const thresholds = interpData.thresholds.map(thresholdObject => ({
            scoreThreshold: thresholdObject.score_threshold,
            interp: getCommonInterpretation(thresholdObject.interpretation),
        }));
        return {
            measure: interpData.measure,
            grade: interpData.grade,
            period: interpData.period,
            goal,
            thresholds,
            maxScore: interpData.max_score,
        };
    });
    return interpretations;
};
